import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useFinishes } from '@/api/finishes';
import { DEFAULT_PANEL_FINISH, componentDims, resolveArmFoamLayers } from '@estimator/shared';
import PanelDimControls from './shared/PanelDimControls';
import {
  Modal, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Button, Input, Divider, Select, SelectItem, Accordion, AccordionItem,
  Autocomplete, AutocompleteItem,
  useDisclosure,
} from '@heroui/react';
import { PanelLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useCreateLineItem, useUpdateLineItem } from '@/api/line-items';
import { useProject } from '@/api/projects';
import { useMaterialsLibrary } from '@/api/materials-library';
import { useHardwareLibrary } from '@/api/hardware-library';
import { useArmTemplates } from '@/api/banquette-arm-templates';
import { useDrawingViewport } from '@/api/drawing-viewports';
import { usePageTileInfo } from '@/api/knowledge';
import ArmDesignSelector from './arm/ArmDesignSelector';
import ArmFramingConfig from './arm/ArmFramingConfig';
import BanquetteViewportSelector from './banquette/BanquetteViewportSelector';
import ArmCostPreview from './arm/ArmCostPreview';
import ArmSlotManager from './arm/ArmSlotManager';
import ArmFinishSummary from './arm/ArmFinishSummary';
import FinishSchedule from './FinishSchedule';
import ComponentNestingPanel from './ComponentNestingPanel';
import { calcArmCostPreview, resolveChannelCount, resolveButtonCount, ProjectCostContext } from '@/utils/arm-cost';
import { useComponentCncCost } from '@/hooks/useComponentCncCost';
import { useEstimateBanquetteLabor } from '@/api/labor-rates';
import { useUpholsteryDesignSettings } from '@/api/upholstery-design-settings';
import { useUpholsteryDesigns } from '@/api/upholstery-designs';
import { resolveDesignTypeAllowancePct, resolveDesignTraits, ALL_FACES, type FaceSelection } from '@estimator/shared';
import FabricCoverageSection from './FabricCoverageSection';
import { toTemplatePayload } from '@/utils/line-item-template';
import { useArmSlotStore } from '@/store/armSlotStore';
import { priceMaterialByUnit } from '@estimator/shared';
import type {
  ArmConfig,
  ArmDesign,
  ArmFraming,
  ArmFoamLayer,
  MaterialLibrary,
} from '@estimator/shared';

interface ArmModalProps {
  isOpen: boolean;
  onClose: () => void;
  areaId: string;
  projectId: string;
  existingLineItemId?: string | null;
  existingConfig?: string | null;
  existingDimensions?: { width: number; height: number; depth: number } | null;
  /** Save as a relation-free component template rather than an area line item. */
  templateMode?: boolean;
}

const DEFAULT_DESIGN: ArmDesign = {
  type: 'plain',
  channel_width: null,
  channel_count_override: null,
  button_tuftings_per_foot: null,
  button_hardware_id: null,
  wire_hardware_id: null,
};

const DEFAULT_FRAMING: ArmFraming = {
  side_panel_material_id: null,
  default_finish_id: null,
  side_panel_finish: DEFAULT_PANEL_FINISH,
  waste_factor: 0.10,
  curved: false,
  curved_waste_factor: 0,
  rails: [
    { name: 'Top Panel', width: 3, width_link: null, length: null, length_link: null, material_id: null, board_run_flipped: false, finish: DEFAULT_PANEL_FINISH },
    { name: 'Bottom Panel', width: 3, width_link: null, length: null, length_link: null, material_id: null, board_run_flipped: false, finish: DEFAULT_PANEL_FINISH },
    { name: 'Front Rail', width: 3, width_link: null, length: null, length_link: null, material_id: null, board_run_flipped: false, finish: DEFAULT_PANEL_FINISH },
    { name: 'Back Rail', width: 3, width_link: null, length: null, length_link: null, material_id: null, board_run_flipped: false, finish: DEFAULT_PANEL_FINISH },
  ],
  additional_panels: [],
};

// Arm defaults: 6" thick, 24" deep, 26" high.
const DEFAULT_WIDTH = 6;
const DEFAULT_DEPTH = 24;
const DEFAULT_HEIGHT = 26;

export default function ArmModal({
  isOpen, onClose, areaId, projectId,
  existingLineItemId, existingConfig, existingDimensions,
  templateMode = false,
}: ArmModalProps) {
  // ── State ──
  const [code, setCode] = useState('ARM');
  const [name, setName] = useState('');
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const [depth, setDepth] = useState(DEFAULT_DEPTH);
  const [height, setHeight] = useState(DEFAULT_HEIGHT);
  const [baseDepth, setBaseDepth] = useState<number | null>(null);
  const [baseHeight, setBaseHeight] = useState<number | null>(null);
  const [baseWidth, setBaseWidth] = useState<number | null>(null);
  const [design, setDesign] = useState<ArmDesign>(DEFAULT_DESIGN);
  const [framing, setFraming] = useState<ArmFraming>(DEFAULT_FRAMING);
  const [foamLayers, setFoamLayers] = useState<ArmFoamLayer[]>([]);
  const [foamWasteFactor, setFoamWasteFactor] = useState(0.10);
  const [foamCurvedWasteFactor, setFoamCurvedWasteFactor] = useState(0);
  const [dacronMaterialId, setDacronMaterialId] = useState<string | null>(null);
  const [dacronWidth, setDacronWidth] = useState(0);
  const [dacronDepth, setDacronDepth] = useState(0);
  const [dacronWasteFactor, setDacronWasteFactor] = useState(0.10);
  const [dacronCurvedWasteFactor, setDacronCurvedWasteFactor] = useState(0);
  const [viewportId, setViewportId] = useState<string | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [fabricDesignPct, setFabricDesignPct] = useState<number | null>(null);
  const [fabricPatternAllowancePct, setFabricPatternAllowancePct] = useState(0);
  const [fabricFaces, setFabricFaces] = useState<FaceSelection>({ ...ALL_FACES });

  // Seeding rule: the first time any panel on this component is linked, pin the
  // base to the current live dimensions. The `prev == null` guard is what makes
  // this a one-time seed rather than an auto-follow -- an auto-follow would hold
  // every delta at zero forever and the feature would silently do nothing.
  useEffect(() => {
    const linked =
      (framing.rails ?? []).some((r) => r.width_link) ||
      (framing.additional_panels ?? []).some((p) => p.width_link || p.height_link) ||
      // A foam layer may track any axis, so any foam link seeds all three bases.
      foamLayers.some((f) => f.width_link || f.depth_link);
    if (!linked) return;
    setBaseDepth((prev) => (prev == null ? depth : prev));
    setBaseHeight((prev) => (prev == null ? height : prev));
    setBaseWidth((prev) => (prev == null ? width : prev));
  }, [framing, foamLayers, depth, height, width]);

  // Resolved foam sizes for the row notes. The numeric inputs keep showing the
  // BASE, so the number you typed is always the number you can edit.
  const resolvedFoam = useMemo(
    () => resolveArmFoamLayers(foamLayers, componentDims({
      width, depth, height,
      base_width: baseWidth, base_depth: baseDepth, base_height: baseHeight,
    })),
    [foamLayers, width, depth, height, baseWidth, baseDepth, baseHeight],
  );

  const slots = useArmSlotStore((s) => s.slots);
  const setSlots = useArmSlotStore((s) => s.setSlots);
  const updateSlot = useArmSlotStore((s) => s.updateSlot);
  const slotManagerModal = useDisclosure();

  // ── API ──
  const createLineItem = useCreateLineItem();
  const updateLineItem = useUpdateLineItem();
  const { data: project } = useProject(projectId);
  const { data: finishes = [] } = useFinishes();
  const { data: materials = [] } = useMaterialsLibrary();
  const { data: hardwareList = [] } = useHardwareLibrary();
  const { data: templates = [] } = useArmTemplates();
  const { data: upholsterySettings } = useUpholsteryDesignSettings();
  // Cached under one key with staleTime: Infinity — UpholsteryDesignSelector
  // below already calls this independently, so this second call costs no
  // extra request. Kept separate rather than prop-drilled through the
  // selector so the cost preview and the selector stay decoupled.
  const { data: designCatalog } = useUpholsteryDesigns();

  // ── Project context for currency conversion + nesting cost mode ──
  const projectCtx = useMemo<ProjectCostContext | undefined>(() => {
    if (!project) return undefined;
    let nestingOverrides: Record<string, string> = {};
    try {
      const raw = (project as any).nesting_cost_overrides;
      if (raw) nestingOverrides = typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch { /* ignore */ }
    return {
      mxn_exchange_rate: (project as any).mxn_exchange_rate ?? null,
      mxn_bank_spread: (project as any).mxn_bank_spread ?? 0,
      nesting_cost_overrides: nestingOverrides,
    };
  }, [project]);

  const isEditMode = !!existingLineItemId;

  const materialsMap = useMemo(() => {
    const map = new Map<string, MaterialLibrary>();
    for (const m of materials) map.set(m.id, m as MaterialLibrary);
    return map;
  }, [materials]);

  const hardwareMap = useMemo(() => {
    const map = new Map<string, { cost_per_unit: number; name: string; unit?: string; currency?: string; iva_percent?: number }>();
    for (const h of hardwareList as any[]) {
      map.set(h.id, {
        cost_per_unit: h.cost_per_unit ?? 0,
        name: h.name,
        unit: h.unit,
        currency: h.currency,
        iva_percent: h.iva_percent,
      });
    }
    return map;
  }, [hardwareList]);

  const hardwareOptions = useMemo(() => {
    return (hardwareList as any[]).map((h) => ({ id: h.id, name: h.name }));
  }, [hardwareList]);

  const { data: viewportData } = useDrawingViewport(viewportId ?? '');
  const { data: tileInfo } = usePageTileInfo(viewportData?.page_id ?? null);
  const pageScalePpi = tileInfo?.scalePixelsPerUnit ?? undefined;

  // The component library (Cria) renders these modals with no project at all.
  // In that context there is no project Nesting Cost Mode to honour, so sheet
  // goods are priced proportionally — matching the server's ex-fabric path.
  const isLibraryContext = templateMode || !projectId;

  // ── Cost Preview ──
  const costPreview = useMemo(() => {
    const config: Partial<ArmConfig> = {
      product_type: 'banquette_arm',
      width,
      depth,
      height,
      base_depth: baseDepth,
      base_width: baseWidth,
      base_height: baseHeight,
      design,
      framing,
      foam_layers: foamLayers,
      foam_waste_factor: foamWasteFactor,
      foam_curved_waste_factor: foamCurvedWasteFactor,
      dacron_material_id: dacronMaterialId,
      dacron_width: dacronWidth,
      dacron_depth: dacronDepth,
      dacron_waste_factor: dacronWasteFactor,
      dacron_curved_waste_factor: dacronCurvedWasteFactor,
      fabric_design_pct: fabricDesignPct,
      fabric_pattern_allowance_pct: fabricPatternAllowancePct,
      fabric_faces: fabricFaces,
      slots,
    };
    return calcArmCostPreview(
      config,
      materialsMap,
      hardwareMap,
      pageScalePpi,
      projectCtx,
      upholsterySettings?.design_allowance_by_type,
      // The component library has no project, so there is no Nesting Cost Mode
      // to honour. Force proportional pricing to match ComponentExFabricService,
      // which already forces used_area for the Cria Layout integration.
      // Keyed off the missing projectId, not templateMode — the library passes
      // templateMode={!editingTemplate}, so it is false while EDITING a template.
      {
        ...(isLibraryContext ? { forceCostMode: 'used_area' as const } : {}),
        finishes,
        designCatalog,
      },
      upholsterySettings?.default_button_hardware_id ?? null,
      upholsterySettings?.default_wire_hardware_id ?? null,
    );
  }, [
    pageScalePpi,
    width, depth, height, baseDepth, baseHeight, baseWidth,
    design, framing,
    foamLayers, foamWasteFactor, foamCurvedWasteFactor,
    dacronMaterialId, dacronWidth, dacronDepth, dacronWasteFactor, dacronCurvedWasteFactor,
    fabricDesignPct, fabricPatternAllowancePct, fabricFaces,
    slots, materialsMap, hardwareMap, projectCtx,
    upholsterySettings,
    isLibraryContext,
    finishes, designCatalog,]);

  // ── CNC cost, amortized over the company reference batch ──
  const cnc = useComponentCncCost(
    costPreview?.nesting_parts ?? [],
    materials as MaterialLibrary[],
  );

  const resolvedDesignPct = useMemo<number | null>(() => {
    if (!upholsterySettings) return null;
    // Depth is the arm's run dimension; resolveButtonCount takes FEET.
    const traits = resolveDesignTraits(design.type, designCatalog);
    const channels = resolveChannelCount(design, depth, height, traits);
    const buttons = resolveButtonCount(design, depth / 12, traits);
    return resolveDesignTypeAllowancePct(
      design.type,
      upholsterySettings.design_allowance_by_type,
      channels,
      buttons,
      traits,
    );
  }, [upholsterySettings, design, depth, height, designCatalog]);

  // ── Labor Estimate (debounced API call) ──
  const laborEstimate = useEstimateBanquetteLabor();
  const laborTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!isOpen) return;
    if (laborTimerRef.current) clearTimeout(laborTimerRef.current);
    laborTimerRef.current = setTimeout(() => {
      const config = {
        product_type: 'banquette_arm',
        width,
        depth,
        height,
        base_depth: baseDepth,
        base_width: baseWidth,
        base_height: baseHeight,
        design,
        framing,
        foam_layers: foamLayers,
        image_scale_ppi: pageScalePpi ?? 0,
        fabric_design_pct: fabricDesignPct,
        fabric_pattern_allowance_pct: fabricPatternAllowancePct,
      fabric_faces: fabricFaces,
        slots,
      };
      laborEstimate.mutate(config);
    }, 500);
    return () => { if (laborTimerRef.current) clearTimeout(laborTimerRef.current); };
  }, [isOpen, width, depth, height, baseDepth, baseHeight, baseWidth, design, framing, foamLayers, slots, pageScalePpi, fabricDesignPct, fabricPatternAllowancePct, fabricFaces]);

  // ── Load existing config ──
  useEffect(() => {
    if (!isOpen) return;
    if (existingConfig) {
      try {
        const ac = JSON.parse(existingConfig);
        setCode(ac.code ?? 'ARM');
        setName(ac.name ?? '');
        setDesign(ac.design ?? DEFAULT_DESIGN);
        setFraming(ac.framing ?? DEFAULT_FRAMING);
        setFoamLayers(ac.foam_layers ?? []);
        setFoamWasteFactor(ac.foam_waste_factor ?? 0.10);
        setFoamCurvedWasteFactor(ac.foam_curved_waste_factor ?? 0);
        setDacronMaterialId(ac.dacron_material_id ?? null);
        setDacronWidth(ac.dacron_width ?? 0);
        setDacronDepth(ac.dacron_depth ?? 0);
        setDacronWasteFactor(ac.dacron_waste_factor ?? 0.10);
        setDacronCurvedWasteFactor(ac.dacron_curved_waste_factor ?? 0);
        setViewportId(ac.viewport_id ?? null);
        setFabricDesignPct(ac.fabric_design_pct ?? null);
        setFabricPatternAllowancePct(ac.fabric_pattern_allowance_pct ?? 0);
        setFabricFaces({ ...ALL_FACES, ...(ac.fabric_faces ?? {}) });
        setSlots(ac.slots ?? []);

        // Line item dimensions win when set; otherwise fall back to the config.
        if (existingDimensions && existingDimensions.width > 0) {
          setWidth(existingDimensions.width);
          setHeight(existingDimensions.height > 0 ? existingDimensions.height : (ac.height ?? DEFAULT_HEIGHT));
          setDepth(existingDimensions.depth > 0 ? existingDimensions.depth : (ac.depth ?? DEFAULT_DEPTH));
        } else {
          setWidth(ac.width ?? DEFAULT_WIDTH);
          setDepth(ac.depth ?? DEFAULT_DEPTH);
          setHeight(ac.height ?? DEFAULT_HEIGHT);
        }
        // Base dims come from the config only, never from existingDimensions.
        // Overwriting them with the line item's live size would zero every
        // delta and silently disable dynamic panels.
        setBaseDepth(ac.base_depth ?? null);
        setBaseWidth(ac.base_width ?? null);
        setBaseHeight(ac.base_height ?? null);
      } catch {
        // Invalid config, use defaults
      }
    }
  }, [isOpen, existingConfig, existingDimensions, setSlots]);

  // ── Template loader ──
  const handleTemplateSelect = useCallback((templateId: string) => {
    setSelectedTemplateId(templateId);
    const tmpl = templates.find((t) => t.id === templateId);
    if (!tmpl) return;
    try {
      const tc = JSON.parse(tmpl.arm_config || '{}');
      setCode(tc.code ?? tmpl.code);
      setName(tc.name ?? tmpl.name);
      if (tc.width != null) setWidth(tc.width);
      if (tc.depth != null) setDepth(tc.depth);
      if (tc.height != null) setHeight(tc.height);
      if (tc.base_depth != null) setBaseDepth(tc.base_depth);
      if (tc.base_width != null) setBaseWidth(tc.base_width);
      if (tc.base_height != null) setBaseHeight(tc.base_height);
      if (tc.design) setDesign(tc.design);
      if (tc.framing) setFraming(tc.framing);
      if (tc.foam_layers) setFoamLayers(tc.foam_layers);
      if (tc.foam_waste_factor != null) setFoamWasteFactor(tc.foam_waste_factor);
      if (tc.foam_curved_waste_factor != null) setFoamCurvedWasteFactor(tc.foam_curved_waste_factor);
      if (tc.dacron_material_id !== undefined) setDacronMaterialId(tc.dacron_material_id);
      if (tc.dacron_width != null) setDacronWidth(tc.dacron_width);
      if (tc.dacron_depth != null) setDacronDepth(tc.dacron_depth);
      if (tc.dacron_waste_factor != null) setDacronWasteFactor(tc.dacron_waste_factor);
      if (tc.dacron_curved_waste_factor != null) setDacronCurvedWasteFactor(tc.dacron_curved_waste_factor);
    } catch { /* ignore */ }
  }, [templates]);

  // ── Build Config ──
  const buildConfig = useCallback((): ArmConfig => {
    return {
      product_type: 'banquette_arm',
      code,
      name: name.trim(),
      width,
      depth,
      height,
      base_depth: baseDepth,
      base_width: baseWidth,
      base_height: baseHeight,
      design,
      framing,
      foam_layers: foamLayers,
      foam_waste_factor: foamWasteFactor,
      foam_curved_waste_factor: foamCurvedWasteFactor,
      dacron_material_id: dacronMaterialId,
      dacron_width: dacronWidth,
      dacron_depth: dacronDepth,
      dacron_waste_factor: dacronWasteFactor,
      dacron_curved_waste_factor: dacronCurvedWasteFactor,
      fabric_design_pct: fabricDesignPct,
      fabric_pattern_allowance_pct: fabricPatternAllowancePct,
      fabric_faces: fabricFaces,
      viewport_id: viewportId,
      image_scale_ppi: pageScalePpi ?? 0,
      slots,
    };
  }, [
    code, name, width, depth, height, baseDepth, baseHeight, baseWidth,
    design, framing, foamLayers, foamWasteFactor, foamCurvedWasteFactor,
    dacronMaterialId, dacronWidth, dacronDepth, dacronWasteFactor, dacronCurvedWasteFactor,
    fabricDesignPct, fabricPatternAllowancePct, fabricFaces, viewportId, slots, pageScalePpi,
  ]);

  // ── Save ──
  const handleSave = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    if (width <= 0 || depth <= 0 || height <= 0) {
      toast.error('Width, depth and height must all be greater than 0');
      return;
    }

    const armConfig = buildConfig();
    const configJson = JSON.stringify(armConfig);

    try {
      if (isEditMode) {
        await updateLineItem.mutateAsync({
          id: existingLineItemId!,
          arm_config: configJson,
          cabinet_code: code,
          description: name.trim(),
          width,
          height,
          depth,
        } as any);
        toast.success('Arm updated');
      } else {
        const createPayload: any = {
          area_id: areaId,
          cabinet_code: code,
          description: name.trim(),
          quantity: 1,
          width, height, depth,
          num_doors: 0, num_drawers: 0, num_shelves: 0,
          arm_config: configJson,
          template_id: null, finish_id: null,
          construction_method_id: null,
          construction_overrides: '{}',
          position_x: 0, position_y: 0, position_z: 0,
          is_in_3d_view: false,
          material_overrides: '{}',
          notes: null, sort_order: 0,
        };
        await createLineItem.mutateAsync(templateMode ? toTemplatePayload(createPayload) : createPayload);
        toast.success(templateMode ? 'Arm template created' : 'Arm added');
      }
      handleReset();
      onClose();
    } catch {
      toast.error(isEditMode ? 'Failed to update arm' : 'Failed to create arm');
    }
  };

  const handleReset = () => {
    setCode('ARM');
    setName('');
    setWidth(DEFAULT_WIDTH);
    setDepth(DEFAULT_DEPTH);
    setHeight(DEFAULT_HEIGHT);
    setBaseDepth(null);
    setBaseWidth(null);
    setBaseHeight(null);
    setDesign(DEFAULT_DESIGN);
    setFraming(DEFAULT_FRAMING);
    setFoamLayers([]);
    setFoamWasteFactor(0.10);
    setDacronMaterialId(null);
    setDacronWidth(0);
    setDacronDepth(0);
    setDacronWasteFactor(0.10);
    setViewportId(null);
    setSelectedTemplateId('');
    useArmSlotStore.getState().clearAll();
  };

  const isPending = createLineItem.isPending || updateLineItem.isPending;

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="4xl"
      scrollBehavior="inside"
      classNames={{
        base: 'bg-[var(--bg-surface)]',
        header: 'border-b border-[var(--border)]',
        footer: 'border-t border-[var(--border)]',
      }}
    >
      <ModalContent>
        <ModalHeader className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
          <PanelLeft size={16} style={{ color: '#e67e22' }} />
          {isEditMode ? 'Edit Arm' : 'New Arm'}
        </ModalHeader>
        <ModalBody className="py-0 px-0 gap-0">
          {/* ── Sticky Cost Bar ── */}
          <div className="sticky top-0 z-10 px-6 py-2" style={{ backgroundColor: 'var(--bg-surface)' }}>
            <ArmCostPreview
              cost={costPreview}
              laborCost={laborEstimate.data?.total_labor_cost}
              laborDetails={laborEstimate.data?.labor_details}
              cncCost={cnc.total_cost_per_unit}
            />
          </div>

          <div className="px-6 py-4 space-y-4">
            {/* 1. Template + Header row */}
            <div className="grid grid-cols-12 gap-3">
              {!isEditMode && templates.length > 0 && (
                <div className="col-span-4">
                  <Select
                    label="Template"
                    selectedKeys={selectedTemplateId ? [selectedTemplateId] : []}
                    onSelectionChange={(keys) => {
                      const id = Array.from(keys)[0] as string;
                      if (id) handleTemplateSelect(id);
                    }}
                    size="sm"
                    placeholder="Load template..."
                    classNames={{ trigger: 'bg-[var(--bg-panel)]' }}
                  >
                    {templates.map((t) => (
                      <SelectItem key={t.id}>{t.code} — {t.name}</SelectItem>
                    ))}
                  </Select>
                </div>
              )}
              <div className={!isEditMode && templates.length > 0 ? 'col-span-2' : 'col-span-3'}>
                <Input label="Code" value={code} onValueChange={setCode} size="sm"
                  classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }} />
              </div>
              <div className={!isEditMode && templates.length > 0 ? 'col-span-6' : 'col-span-9'}>
                <Input label="Name *" value={name} onValueChange={setName} size="sm" isRequired
                  classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }} />
              </div>
            </div>

            {/* 2. Dimensions — an arm is one discrete unit, no LF and no sections */}
            <div>
              <h3 className="text-xs font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Dimensions</h3>
              <div className="grid grid-cols-6 gap-3">
                <Input label="Width" type="number" value={width.toString()}
                  onValueChange={(v) => setWidth(Number(v) || 0)} size="sm"
                  classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
                  description="Thickness"
                  endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>in</span>} />
                <Input label="Depth" type="number" value={depth.toString()}
                  onValueChange={(v) => setDepth(Number(v) || 0)} size="sm"
                  classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
                  description="Front to back — the design axis"
                  endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>in</span>} />
                <Input label="Height" type="number" value={height.toString()}
                  onValueChange={(v) => setHeight(Number(v) || 0)} size="sm"
                  classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
                  endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>in</span>} />
                <Input label="Base Height" type="number"
                  value={(baseHeight ?? height).toString()}
                  onValueChange={(v) => setBaseHeight(Number(v) || 0)} size="sm"
                  classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
                  description="Dynamic panel reference"
                  endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>in</span>} />
                <Input label="Base Depth" type="number"
                  value={(baseDepth ?? depth).toString()}
                  onValueChange={(v) => setBaseDepth(Number(v) || 0)} size="sm"
                  classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
                  description="Dynamic panel reference"
                  endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>in</span>} />
                <Input label="Base Width" type="number"
                  value={(baseWidth ?? width).toString()}
                  onValueChange={(v) => setBaseWidth(Number(v) || 0)} size="sm"
                  classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
                  description="Dynamic foam reference"
                  endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>in</span>} />
              </div>
            </div>

            <Divider className="bg-[var(--border)]" />

            {/* 3. Design Type Selector */}
            <ArmDesignSelector
              design={design}
              onChange={setDesign}
              depthInches={depth}
              heightInches={height}
              hardwareOptions={hardwareOptions}
            />

            <Divider className="bg-[var(--border)]" />

            {/* 4. Framing Configuration */}
            <Accordion variant="bordered" defaultExpandedKeys={['framing']}>
              <AccordionItem key="framing" title="Framing Configuration"
                classNames={{ base: 'border-[var(--border)]', title: 'text-xs', trigger: 'py-2' }}>
                <ArmFramingConfig
                  framing={framing}
                  materials={materials as MaterialLibrary[]}
                  onChange={setFraming}
                  depthInches={depth}
                  heightInches={height}
                  dims={{
                    width: width,
                    depth: depth,
                    height: height,
                    run_axis: 'depth' as const,
                    base_width: baseWidth,
                    base_depth: baseDepth,
                    base_height: baseHeight,
                  }}
                />
              </AccordionItem>
            </Accordion>

            <Divider className="bg-[var(--border)]" />

            {/* 5. Foam Layers — explicit width x depth per layer */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold" style={{ color: '#e67e22' }}>Foam Layers</h3>
                <div className="flex items-center gap-2">
                  <Input
                    label="Waste"
                    type="number"
                    value={(foamWasteFactor * 100).toString()}
                    onValueChange={(v) => setFoamWasteFactor((Number(v) || 0) / 100)}
                    size="sm"
                    className="w-20"
                    classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
                    endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>%</span>}
                  />
                  <Input
                    label="Curved Waste"
                    type="number"
                    value={(foamCurvedWasteFactor * 100).toString()}
                    onValueChange={(v) => setFoamCurvedWasteFactor((Number(v) || 0) / 100)}
                    size="sm"
                    className="w-24"
                    classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
                    endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>%</span>}
                  />
                  <Button size="sm" variant="light" onPress={() => {
                    setFoamLayers([...foamLayers, { name: `Foam ${foamLayers.length + 1}`, width, width_link: null, depth, depth_link: null, material_id: null }]);
                  }}>
                    + Add Layer
                  </Button>
                </div>
              </div>
              {framing.curved && foamCurvedWasteFactor > 0 && (
                <div className="text-[10px] mb-2" style={{ color: 'var(--text-muted)' }}>
                  Effective foam waste: {((foamWasteFactor + foamCurvedWasteFactor) * 100).toFixed(1)}%
                </div>
              )}
              <div className="space-y-2">
                {foamLayers.map((layer, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input
                      value={layer.name}
                      onValueChange={(v) => {
                        const updated = [...foamLayers];
                        updated[i] = { ...updated[i], name: v };
                        setFoamLayers(updated);
                      }}
                      size="sm"
                      className="w-36"
                      classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
                      placeholder="Name"
                    />
                    <Input
                      type="number"
                      value={layer.width.toString()}
                      onValueChange={(v) => {
                        const updated = [...foamLayers];
                        updated[i] = { ...updated[i], width: Number(v) || 0 };
                        setFoamLayers(updated);
                      }}
                      size="sm"
                      className="w-20"
                      classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
                      endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>w</span>}
                    />
                    <PanelDimControls
                      label="Width"
                      link={layer.width_link ?? null}
                      onChange={(link) => {
                        const updated = [...foamLayers];
                        updated[i] = { ...updated[i], width_link: link };
                        setFoamLayers(updated);
                      }}
                    />
                    <Input
                      type="number"
                      value={layer.depth.toString()}
                      onValueChange={(v) => {
                        const updated = [...foamLayers];
                        updated[i] = { ...updated[i], depth: Number(v) || 0 };
                        setFoamLayers(updated);
                      }}
                      size="sm"
                      className="w-20"
                      classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
                      endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>d</span>}
                    />
                    {/* No Auto option: an arm has no run, so depth is always explicit. */}
                    <PanelDimControls
                      label="Depth"
                      link={layer.depth_link ?? null}
                      onChange={(link) => {
                        const updated = [...foamLayers];
                        updated[i] = { ...updated[i], depth_link: link };
                        setFoamLayers(updated);
                      }}
                    />
                    <Autocomplete
                      selectedKey={layer.material_id ?? undefined}
                      onSelectionChange={(key) => {
                        const updated = [...foamLayers];
                        updated[i] = { ...updated[i], material_id: (key as string) || null };
                        setFoamLayers(updated);
                      }}
                      size="sm"
                      placeholder="Material"
                      className="flex-1"
                      inputProps={{ classNames: { inputWrapper: 'bg-[var(--bg-panel)]' } }}
                    >
                      {materials.map((m: any) => (
                        <AutocompleteItem key={m.id}>{m.name}</AutocompleteItem>
                      ))}
                    </Autocomplete>
                    <Button
                      isIconOnly
                      size="sm"
                      variant="light"
                      onPress={() => setFoamLayers(foamLayers.filter((_, idx) => idx !== i))}
                    >
                      <span style={{ color: 'var(--text-muted)' }}>🗑</span>
                    </Button>
                    {resolvedFoam[i] && (
                      <span
                        className="text-[10px] flex-shrink-0 w-32 text-right"
                        style={{ color: resolvedFoam[i].collapsed ? '#e67e22' : 'var(--text-muted)' }}
                      >
                        {resolvedFoam[i].collapsed
                          ? 'collapsed'
                          : `${resolvedFoam[i].width} x ${resolvedFoam[i].depth} in · ${((resolvedFoam[i].width * resolvedFoam[i].depth) / 144).toFixed(1)} sqft`}
                      </span>
                    )}
                  </div>
                ))}
                {foamLayers.length === 0 && (
                  <div className="text-xs py-2 text-center" style={{ color: 'var(--text-muted)' }}>
                    No foam layers. Click "+ Add Layer" to add.
                  </div>
                )}
              </div>
            </div>

            {/* 6. Dacron — explicit width x depth */}
            <div>
              <h3 className="text-xs font-semibold mb-2" style={{ color: '#e67e22' }}>Dacron</h3>
              <div className="grid grid-cols-5 gap-3">
                <Autocomplete
                  label="Dacron Material"
                  selectedKey={dacronMaterialId ?? undefined}
                  onSelectionChange={(key) => {
                    setDacronMaterialId((key as string) || null);
                  }}
                  size="sm"
                  placeholder="Select dacron material..."
                  inputProps={{ classNames: { inputWrapper: 'bg-[var(--bg-panel)]' } }}
                >
                  {materials.map((m: any) => (
                    <AutocompleteItem key={m.id}>{m.name}</AutocompleteItem>
                  ))}
                </Autocomplete>
                <Input
                  label="Dacron Width"
                  type="number"
                  value={dacronWidth.toString()}
                  onValueChange={(v) => setDacronWidth(Number(v) || 0)}
                  size="sm"
                  classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
                  endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>in</span>}
                />
                <Input
                  label="Dacron Depth"
                  type="number"
                  value={dacronDepth.toString()}
                  onValueChange={(v) => setDacronDepth(Number(v) || 0)}
                  size="sm"
                  classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
                  endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>in</span>}
                />
                <Input
                  label="Cost / sqft"
                  value={(() => {
                    if (!dacronMaterialId) return '—';
                    const m = materials.find((mat: any) => mat.id === dacronMaterialId) as any;
                    if (!m) return '—';
                    // One square foot of this material, costed the way the
                    // cost helpers cost it. Going through priceMaterialByUnit
                    // keeps this readout honest for every unit spelling —
                    // comparing `m.unit` against 'sq ft' / 'lin ft' literals
                    // here showed a linear_foot material's per-LF price as if
                    // it were already per-sqft.
                    const perSqft = priceMaterialByUnit(1, m, 0, { pieceQty: 1 });
                    if (perSqft.warnings.length > 0) return '—';
                    return `$${perSqft.cost.toFixed(perSqft.cost < 1 ? 4 : 2)}`;
                  })()}
                  isReadOnly
                  size="sm"
                  classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
                  description="From material"
                />
                <Input
                  label="Dacron Waste Factor"
                  type="number"
                  value={(dacronWasteFactor * 100).toString()}
                  onValueChange={(v) => setDacronWasteFactor((Number(v) || 0) / 100)}
                  size="sm"
                  classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
                  endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>%</span>}
                />
                <Input
                  label="Dacron Curved Waste"
                  type="number"
                  value={(dacronCurvedWasteFactor * 100).toString()}
                  onValueChange={(v) => setDacronCurvedWasteFactor((Number(v) || 0) / 100)}
                  size="sm"
                  classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
                  endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>%</span>}
                />
              </div>
              {framing.curved && dacronCurvedWasteFactor > 0 && (
                <div className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>
                  Effective dacron waste: {((dacronWasteFactor + dacronCurvedWasteFactor) * 100).toFixed(1)}%
                </div>
              )}
            </div>

            <Divider className="bg-[var(--border)]" />

            {/* 7. Viewport + Slot Manager */}
            <div className="grid grid-cols-2 gap-4">
              <BanquetteViewportSelector
                projectId={projectId}
                viewportId={viewportId}
                onViewportChange={(id) => setViewportId(id)}
              />
              <div className="flex flex-col justify-end">
                <Button
                  fullWidth
                  variant="bordered"
                  onPress={slotManagerModal.onOpen}
                  isDisabled={!viewportId}
                  style={{ borderColor: '#e67e22', color: '#e67e22' }}
                >
                  Open Slot Manager ({slots.length} slot{slots.length !== 1 ? 's' : ''})
                </Button>
              </div>
            </div>

            {/* Finish assignments for slots with materials requiring finish */}
            {slots.length > 0 && (
              <>
                <Divider className="bg-[var(--border)]" />
                <ArmFinishSummary
                  slots={slots}
                  materials={materials as MaterialLibrary[]}
                  onUpdateSlot={updateSlot}
                />
              </>
            )}

            <Divider className="bg-[var(--border)]" />

            {/* Fabric Allowances */}
            {(() => {
              const isFabricMat = (m: any) => !!(m?.fabric_subtype) || ((m?.material_type ?? '') as string).toLowerCase() === 'fabric';
              const hasFabricSlot = slots.some((s) => {
                if (!s.material_id) return false;
                return isFabricMat(materialsMap.get(s.material_id));
              });
              if (!hasFabricSlot) return null;
              const firstFabricMat = slots
                .filter((s) => s.material_id)
                .map((s) => materialsMap.get(s.material_id!))
                .find((m) => isFabricMat(m));
              const matDefault = (firstFabricMat as any)?.fabric_design_pct ?? 0;
              return (
                <div className="flex gap-3">
                  <Input
                    label="Design Allowance (%)"
                    type="number"
                    size="sm"
                    className="w-44"
                    value={fabricDesignPct != null ? String(fabricDesignPct * 100) : ''}
                    placeholder={
                      resolvedDesignPct != null
                        ? `Inherited: ${(resolvedDesignPct * 100).toFixed(0)}%`
                        : `Mat default: ${(matDefault * 100).toFixed(0)}%`
                    }
                    onValueChange={(v) => {
                      if (v === '') { setFabricDesignPct(null); return; }
                      setFabricDesignPct((Number(v) || 0) / 100);
                    }}
                    description="Overrides design type setting"
                  />
                  <Input
                    label="Pattern Allowance (%)"
                    type="number"
                    size="sm"
                    className="w-44"
                    value={String(fabricPatternAllowancePct * 100)}
                    onValueChange={(v) => setFabricPatternAllowancePct((Number(v) || 0) / 100)}
                    description="Pattern repeat allowance"
                  />
                </div>
              );
            })()}

            <FabricCoverageSection
              productType="banquette_arm"
              dims={{ width_in: width, height_in: height, depth_in: depth }}
              value={fabricFaces}
              onChange={setFabricFaces}
            />

            {/* Finish Schedule */}
            <Accordion variant="bordered">
              <AccordionItem
                key="finish-schedule"
                title="Finish Schedule"
                classNames={{ base: 'border-[var(--border)]', title: 'text-xs', trigger: 'py-2' }}
              >
                <FinishSchedule
                  slotDetails={costPreview?.slot_details ?? []}
                  materials={materials as MaterialLibrary[]}
                  fabricPackings={costPreview?.fabric_packings ?? []}
                />
              </AccordionItem>
              <AccordionItem
                key="nesting-preview"
                title="Nesting Preview"
                classNames={{ base: 'border-[var(--border)]', title: 'text-xs', trigger: 'py-2' }}
              >
                <ComponentNestingPanel
                  parts={costPreview?.nesting_parts ?? []}
                  materials={materials as MaterialLibrary[]}
                  cnc={cnc}
                />
              </AccordionItem>
            </Accordion>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={onClose}>Cancel</Button>
          <Button
            onPress={handleSave}
            isLoading={isPending}
            style={{ backgroundColor: '#e67e22', color: 'white' }}
          >
            {isEditMode ? 'Update' : 'Create'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>

    {/* Slot Manager fullscreen modal */}
    {viewportId && (
      <ArmSlotManager
        isOpen={slotManagerModal.isOpen}
        onClose={slotManagerModal.onClose}
        viewportId={viewportId}
        projectId={projectId}
        widthInches={width}
        depthInches={depth}
        heightInches={height}
      />
    )}
    </>
  );
}
